import { msg } from '@gonfalon/messages/msg';
import {
  dataSourceSqlQueryTooLongMessage,
  isDataSourceSqlQueryTooLong,
  MAX_DATA_SOURCE_TABLE_LENGTH,
} from '@gonfalon/metrics/constants';
import { z } from 'zod';

export const quickEditFields = z.object({
  name: z
    .string()
    .min(1, { error: () => msg('metrics.assignmentDataSourceForm.required') })
    .max(128, { error: () => msg('metrics.assignmentDataSourceForm.nameTooLong') }),
  maintainerId: z.string().min(1, { error: () => msg('metrics.assignmentDataSourceForm.maintainerRequired') }),
  archived: z.boolean(),
  description: z
    .string()
    .max(1024, { error: () => msg('metrics.assignmentDataSourceForm.descriptionTooLong') })
    .optional(),
});

export const columnSchema = z.object({
  name: z.string(),
  type: z.string(),
  nullable: z.boolean().optional(),
  length: z.number().optional(),
  precision: z.number().optional(),
  scale: z.number().optional(),
});

export const columnsSchema = z
  .array(columnSchema)
  .min(1, { error: () => msg('metrics.assignmentDataSourceForm.columnsRequired') });

export const contextMappingsSchema = z
  .array(
    z.tuple([
      z.string().min(1, { error: () => msg('metrics.assignmentDataSourceForm.contextKindRequired') }),
      z.string().min(1, { error: () => msg('metrics.assignmentDataSourceForm.contextColumnRequired') }),
    ]),
  )
  .min(1, { error: () => msg('metrics.assignmentDataSourceForm.contextMappingRequired') })
  .default([['', '']]);

export const timestampColumnSchema = z
  .string()
  .min(1, { error: () => msg('metrics.assignmentDataSourceForm.timestampColumnRequired') });

export const warehouseDataSourceFields = z
  .object({
    key: z
      .string()
      .min(1, { error: () => msg('metrics.assignmentDataSourceForm.required') })
      .max(128, { error: () => msg('metrics.assignmentDataSourceForm.keyTooLong') })
      .regex(/^[a-zA-Z0-9][a-zA-Z0-9._-]*$/, { error: () => msg('metrics.assignmentDataSourceForm.keyPattern') }),
    integrationConfig: z.object({
      key: z.string().min(1, { error: () => msg('metrics.assignmentDataSourceForm.integrationRequired') }),
      environmentKey: z
        .string()
        .min(1, { error: () => msg('metrics.assignmentDataSourceForm.environmentKeyRequired') }),
      id: z.string().optional(),
      projectKey: z.string(),
    }),
    inputMethod: z.enum(['query', 'table']).default('query'),
    sqlQuery: z
      .string()
      .refine((value) => !isDataSourceSqlQueryTooLong(value), { error: dataSourceSqlQueryTooLongMessage })
      .optional(),
    dbTable: z
      .string()
      .max(MAX_DATA_SOURCE_TABLE_LENGTH, {
        error: () => msg('metrics.assignmentDataSourceForm.tableNameTooLong', { max: MAX_DATA_SOURCE_TABLE_LENGTH }),
      })
      .optional(),
    tags: z.array(z.string()),
  })
  .extend(quickEditFields.shape);

export const requireActiveInputField = (
  data: { dataSource: Pick<z.infer<typeof warehouseDataSourceFields>, 'sqlQuery' | 'dbTable' | 'inputMethod'> },
  ctx: z.RefinementCtx,
) => {
  const { sqlQuery, dbTable, inputMethod } = data.dataSource;

  if (inputMethod === 'table') {
    if (!dbTable?.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['dataSource', 'dbTable'],
        message: msg('metrics.assignmentDataSourceForm.required'),
      });
    }
    return;
  }

  if (!sqlQuery?.trim()) {
    ctx.addIssue({
      code: 'custom',
      path: ['dataSource', 'sqlQuery'],
      message: msg('metrics.assignmentDataSourceForm.required'),
    });
  }
};
